import {
	StackCompat as Stack,
	TextCompat as Text,
	useTheme,
} from 'components/FluentUICompat';
import React, {FormEvent, useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {
	useDeepSearchParagraphsLazyQuery,
	useFullTextSearchPogisLazyQuery,
	useFullTextSearchRegulationsLazyQuery,
	useFullTextSearchRegulatoryDocumentsLazyQuery,
	useFullTextSearchRequirementsLazyQuery,
} from './hooks';
import {useState} from 'react';
import {SearchResultLoading} from './components/SearchCards/SearchResultCards';
import {checkSearchValid} from 'components/TextSearchBar';
import {RequirmentSearchResult} from './components/SearchResult/SearchResultsRequirement';
import {RegulationSearchResult} from './components/SearchResult/SearchResultsRegulation';
import {useCommand} from 'hooks';
import {v4 as uuidv4} from 'uuid';
import {RegDocumentSearchResult} from './components/SearchResult/SearchResultsRegDocument';
import {PogisSearchResult} from './components/SearchResult/SearchResultsPogis';
import {DeepSearchResult} from './components/SearchResult/SearchResultsDeep';
import {SearchPivot} from './components/SearchPivot/SearchPivot';
import {SearchTopNumber} from './components/SearchTopNumber/SearchTopNumber';

export type SearchStateEntity = {
	searchText: string;
	top: number;
};
export type SearchState = {
	regulations?: SearchStateEntity;
	regulatorydocuments?: SearchStateEntity;
	requirements?: SearchStateEntity;
	pogisdocuments?: SearchStateEntity;
	paragraphs?: SearchStateEntity;
};

const NUM_DEFAULT_TOP_SUGG = 25;

export const SearchPage: React.FC = () => {
	const {t} = useTranslation('features/search');

	// Use separate lazy queries for each search
	const [
		runRequirementSearch,
		{data: requirementData, loading: requirementsLoading},
	] = useFullTextSearchRequirementsLazyQuery();

	const [
		runRegulationSearch,
		{data: regulationData, loading: regulationsLoading},
	] = useFullTextSearchRegulationsLazyQuery();

	const [runDocumentsSearch, {data: documentData, loading: documentsLoading}] =
		useFullTextSearchRegulatoryDocumentsLazyQuery();

	const [runPogisSearch, {data: pogisData, loading: pogisLoading}] =
		useFullTextSearchPogisLazyQuery();

	const [runDeepSearch, {data: deepSearchData, loading: deepSearchLoading}] =
		useDeepSearchParagraphsLazyQuery();

	const [currentSearchState, setCurrentSearchState] = useState<SearchState>({});
	const [currentTop, setCurrentTop] = useState(NUM_DEFAULT_TOP_SUGG);
	const [currentViewKey, setCurrentViewKey] = useState('');
	const [isResultCardFormat, setIsResultCardFormat] = useState(true);

	const [searchParams] = useSearchParams();
	const searchText = searchParams.get('text') || '';
	const searchView = (searchParams.get('entity') || 'all').toLowerCase();

	useEffect(() => {
		setCurrentViewKey(searchView);

		if (checkSearchValid(searchText)) {
			const isFetchNeeded = getCheckIfFetchNeeded(
				searchText,
				searchView,
				currentTop,
			);

			const fetchRegulations = isFetchNeeded(
				'regulations',
				currentSearchState.regulations,
			);

			const fetchRegDocs = isFetchNeeded(
				'regulatorydocuments',
				currentSearchState.regulatorydocuments,
			);

			const fetchRequirements = isFetchNeeded(
				'requirements',
				currentSearchState.requirements,
			);

			const fetchPOGIS = isFetchNeeded(
				'pogisdocuments',
				currentSearchState.pogisdocuments,
			);

			const fetchParagraphs = isFetchNeeded(
				'paragraphs',
				currentSearchState.paragraphs,
			);

			const updateSearchState: boolean =
				fetchRegulations ||
				fetchRegDocs ||
				fetchRequirements ||
				fetchPOGIS ||
				fetchParagraphs;

			const newSearchStateEntity: SearchStateEntity = {
				searchText,
				top: currentTop,
			};

			const newSearchState: SearchState = {
				regulations: fetchRegulations
					? newSearchStateEntity
					: currentSearchState.regulations,
				regulatorydocuments: fetchRegDocs
					? newSearchStateEntity
					: currentSearchState.regulatorydocuments,
				requirements: fetchRequirements
					? newSearchStateEntity
					: currentSearchState.requirements,
				pogisdocuments: fetchPOGIS
					? newSearchStateEntity
					: currentSearchState.pogisdocuments,
				paragraphs: fetchParagraphs
					? newSearchStateEntity
					: currentSearchState.paragraphs,
			};

			const variables = {searchString: searchText, limit: currentTop};

			if (fetchRegulations) {
				runRegulationSearch({variables});
			}
			if (fetchRegDocs) {
				runDocumentsSearch({variables});
			}
			if (fetchRequirements) {
				runRequirementSearch({variables});
			}
			if (fetchPOGIS) {
				runPogisSearch({variables});
			}
			if (fetchParagraphs) {
				runDeepSearch({variables});
			}

			if (updateSearchState) {
				setCurrentSearchState(newSearchState);
			}
		}
	}, [searchText, searchView, currentTop, currentSearchState]);

	useCommand(
		{
			key: uuidv4(),
			text: t(isResultCardFormat ? 'ListStyle' : 'CardStyle'),
			priority: 0,
			iconProps: {
				iconName: isResultCardFormat ? 'BulletedList2' : 'Packages',
			},
			onClick() {
				setIsResultCardFormat(!isResultCardFormat);
				if (currentViewKey === 'all') {
					setCurrentViewKey('requirements');
				}
			},
		},
		[isResultCardFormat, currentViewKey],
	);

	const requirmentSearchResult = useMemo(() => {
		const propsResult = {
			currentViewKey,
			isResultCardFormat,
			loading: requirementsLoading,
			requirementData,
		};
		return <RequirmentSearchResult {...propsResult} />;
	}, [
		currentViewKey,
		isResultCardFormat,
		requirementsLoading,
		requirementData,
	]);

	const propsSearchResultBasic = useMemo(
		() => ({
			currentViewKey,
			isResultCardFormat,
		}),
		[currentViewKey, isResultCardFormat],
	);

	const regulationSearchResult = useMemo(() => {
		const propsResult = {
			...propsSearchResultBasic,
			loading: regulationsLoading,
			regulationData,
		};
		return <RegulationSearchResult {...propsResult} key={uuidv4()} />;
	}, [propsSearchResultBasic, regulationsLoading, regulationData]);

	const regDocumentSearchResult = useMemo(() => {
		const propsResult = {
			...propsSearchResultBasic,
			loading: documentsLoading,
			documentData,
		};
		return <RegDocumentSearchResult {...propsResult} />;
	}, [propsSearchResultBasic, documentsLoading, documentData]);

	const pogisSearchResult = useMemo(() => {
		const propsResult = {
			...propsSearchResultBasic,
			loading: pogisLoading,
			pogisData,
		};
		return <PogisSearchResult {...propsResult} />;
	}, [propsSearchResultBasic, pogisLoading, pogisData]);

	const deepSearchResult = useMemo(() => {
		const propsResult = {
			...propsSearchResultBasic,
			loading: deepSearchLoading,
			deepSearchData,
		};
		return <DeepSearchResult {...propsResult} />;
	}, [propsSearchResultBasic, deepSearchLoading, deepSearchData]);

	const propsTopNumber = {currentTop, setCurrentTop};
	const propsPivot = {
		searchText,
		currentViewKey,
		currentTop,
		isResultCardFormat,
		setCurrentViewKey,
		currentSearchState,
		requirementsLoading,
		regulationsLoading,
		documentsLoading,
		pogisLoading,
		deepSearchLoading,
		requirementData,
		regulationData,
		documentData,
		pogisData,
		deepSearchData,
	};
	return (
		<>
			<SearchPivot {...propsPivot} />
			<Stack tokens={{childrenGap: 8}}>
				{requirmentSearchResult}
				{regulationSearchResult}
				{regDocumentSearchResult}
				{pogisSearchResult}
				{deepSearchResult}
			</Stack>
			<SearchTopNumber {...propsTopNumber} />
		</>
	);
};

const getCheckIfFetchNeeded =
	(searchText: string, searchView: string, currentTop: number) =>
	(entityName: string, searchStateEntity?: SearchStateEntity) => {
		return (
			['all', entityName].includes(searchView) &&
			getCheckIfSearchNeeded(searchText, currentTop, searchStateEntity)
		);
	};

export const getCheckIfSearchNeeded = (
	searchText: string,
	currentTop: number,
	searchStateEntity?: SearchStateEntity,
) => {
	if (searchStateEntity) {
		return (
			searchStateEntity.searchText !== searchText ||
			searchStateEntity.top !== currentTop
		);
	}
	return true;
};
